import type { AuthSession, AuthTokens, AuthUser } from '@repo/api-contracts';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  readonly user: AuthUser | null;
  readonly tokens: AuthTokens | null;
  readonly isAuthenticated: boolean;
}

interface AuthActions {
  setSession: (session: AuthSession) => void;
  setTokens: (tokens: AuthTokens) => void;
  clear: () => void;
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
};

/** persist 키 — 탭 간 동기화(cross-tab-sync)가 storage 이벤트 필터에 쓴다. */
export const AUTH_STORAGE_KEY = 'superstructure.admin.auth';

/**
 * 인증 세션 스토어.
 *
 * 서버 상태는 TanStack Query 가 관리하고, 이 스토어는 "로그인 여부와 토큰"이라는
 * 순수 클라이언트 상태만 보관한다.
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setSession: (session) =>
        set({ user: session.user, tokens: session.tokens, isAuthenticated: true }),

      setTokens: (tokens) => set((state) => ({ ...state, tokens })),

      clear: () => set({ ...initialState }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      // 파생 상태(isAuthenticated)는 저장하지 않고 복원 시 다시 계산한다.
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
      merge: (persisted, current) => {
        const saved = persisted as Partial<AuthState> | undefined;

        return {
          ...current,
          user: saved?.user ?? null,
          tokens: saved?.tokens ?? null,
          isAuthenticated: Boolean(saved?.tokens?.accessToken),
        };
      },
    },
  ),
);

/** 컴포넌트 밖(tRPC 링크 등)에서 토큰을 읽기 위한 헬퍼. */
export const getAccessToken = (): string | null =>
  useAuthStore.getState().tokens?.accessToken ?? null;

/** 재발급기(token-refresher)가 리프레시 토큰을 읽기 위한 헬퍼. */
export const getRefreshToken = (): string | null =>
  useAuthStore.getState().tokens?.refreshToken ?? null;
